import React from "react";
import { Box, ButtonBase, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import { IBotData, IPerformanceDataPoint } from "../../shared/types";
import {
  formatCompactCurrency,
  formatCurrency,
  formatDate,
  formatPairPrice,
  formatPercent,
  formatQuantity,
  formatSignedUsd,
  formatUsd,
  formatSignedCurrency,
  formatSignedPercent,
  formatSignedPp,
} from "../../utils/formatters";
import { calculateTotalVolume } from "../../features/calculateTotalVolume";
import { cumulativeBuybackTrend, cumulativeFeesTrend, vsHodlPp, weeklyVolumeTrend } from "../../features/overviewSeries";
import { useAuth } from "../../hooks/useAuth";
import { SurfaceCard } from "../../components/ui/SurfaceCard";
import { StatTile } from "../../components/ui/StatTile";
import CustomTooltip, { TooltipSection } from "../../components/cards/subcomponents/CustomTooltip";
import PerformanceChart from "../../components/charts/Performance/PerformanceChart";
import { tokens, touchHitArea } from "../../theme";
import { useToneColor } from "../../components/ui/StatusPill";
import {
  BOT_STATE_LABEL,
  BOT_STATE_TONE,
  STATE_ORDER,
  botState,
  MARKET_RESIDUAL_HELP,
  aggregateReturn,
  capitalBasis,
  hasReturnBasis,
  hodlBenchmarkLabel,
  countStates,
  isInRange,
  pnlBreakdownDisplayed,
} from "../../utils/botMetrics";

type ChipTone = "positive" | "negative" | "gold" | "neutral";

/** Rounded figure chip used under the hero number. */
const HeroChip: React.FC<{ tone: ChipTone; children: React.ReactNode; icon?: React.ReactNode }> = ({
  tone,
  children,
  icon,
}) => {
  const theme = useTheme();
  const color =
    tone === "positive"
      ? theme.palette.success.main
      : tone === "negative"
      ? theme.palette.error.main
      : tone === "gold"
      ? tokens.goldLight
      : theme.palette.text.secondary;
  const base = tone === "gold" ? tokens.gold : color;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        height: { xs: 30, md: 34 },
        pl: icon ? 1.125 : 1.5,
        pr: 1.5,
        borderRadius: 999,
        fontSize: { xs: "0.75rem", md: "0.8125rem" },
        fontWeight: 650,
        whiteSpace: "nowrap",
        color,
        bgcolor: alpha(base, 0.12),
        border: `1px solid ${alpha(base, 0.34)}`,
        fontVariantNumeric: "tabular-nums",
        position: "relative",
        ...touchHitArea,
      }}
    >
      {icon}
      {children}
    </Box>
  );
};

export interface AttentionItem {
  key: string;
  label: string;
  count: number;
  tone: "error" | "warning";
  /** Takes the user to the affected bot(s): opens the bot when there is one, else jumps to the first. */
  onSelect: () => void;
}

/** Admin triage row inside the hero: most severe first, each chip jumps to the details. */
const AttentionRow: React.FC<{ items: AttentionItem[] }> = ({ items }) => (
  <Box role="group" aria-label="Needs attention" sx={{ mt: { xs: 2.5, md: 3 } }}>
    <Typography sx={{ fontSize: "0.75rem", fontWeight: 650, color: "text.secondary", mb: 1 }}>
      Needs attention
    </Typography>
    <Stack direction="row" sx={{ flexWrap: "wrap", columnGap: 0.75, rowGap: { xs: 0.75, md: 0.75 }, "@media (pointer: coarse)": { rowGap: 1.5 } }}>
      {items.map((a) => (
        <ButtonBase
          key={a.key}
          onClick={a.onSelect}
          sx={{
            gap: 0.75,
            px: 1.25,
            height: 32,
            borderRadius: 999,
            fontSize: "0.8125rem",
            fontWeight: 650,
            color: `${a.tone}.main`,
            bgcolor: (t) => alpha(t.palette[a.tone].main, 0.1),
            border: (t) => `1px solid ${alpha(t.palette[a.tone].main, 0.3)}`,
            transition: "background-color 160ms ease",
            "&:hover": { bgcolor: (t) => alpha(t.palette[a.tone].main, 0.18) },
            ...touchHitArea,
          }}
        >
          <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: `${a.tone}.main`, flexShrink: 0 }} />
          {a.label}
          <Box component="span" sx={{ fontVariantNumeric: "tabular-nums", color: "text.primary" }}>
            {a.count}
          </Box>
        </ButtonBase>
      ))}
    </Stack>
  </Box>
);

/**
 * One segment per bot, same states as everywhere else. The portfolio groups them most severe
 * first (red → gold → green); a family keeps bot order (Bot 1, 2, 3) so each segment maps to a tab.
 */
const BotStateStrip: React.FC<{ bots: IBotData[]; order?: "severity" | "bot" }> = ({ bots, order = "severity" }) => {
  const resolveTone = useToneColor();
  const byName = (a: IBotData, b: IBotData) =>
    a.stats.botName.localeCompare(b.stats.botName, undefined, { numeric: true });
  const sorted = [...bots].sort((a, b) =>
    order === "bot"
      ? byName(a, b)
      : STATE_ORDER.indexOf(botState(a.stats)) - STATE_ORDER.indexOf(botState(b.stats)) || byName(a, b)
  );
  return (
    <Stack direction="row" spacing={0.5} sx={{ height: 8 }}>
      {sorted.map((b) => {
        const st = botState(b.stats);
        const color = resolveTone(BOT_STATE_TONE[st]);
        return (
          <Tooltip key={b.stats.botName} title={`${b.stats.botName} · ${BOT_STATE_LABEL[st]}`}>
            <Box
              sx={{
                flex: 1,
                minWidth: 4,
                borderRadius: 999,
                bgcolor: color,
                boxShadow: `0 0 8px ${alpha(color, 0.45)}`,
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
};

export const AggregateStatsCard: React.FC<{
  bots: IBotData[];
  isHomeView?: boolean;
  /** Indexed LP vs HODL history for the hero chart. */
  performanceData?: IPerformanceDataPoint[];
  /** Admin triage chips shown inside the hero. */
  attention?: AttentionItem[];
}> = ({ bots, isHomeView, performanceData = [], attention = [] }) => {
  const { groups } = useAuth();
  const isAdmin = groups.includes("admin");

  const aggregates = React.useMemo(() => {
    const initial = {
      token: "",
      totalCapital: 0,
      totalPnlUsd: 0,
      botsInRange: 0,
      poolFees: 0,
      initialCapital: 0,
      botPremiumsUSD: 0,
      manualPremiums: 0,
      buybacks: 0,
      lendingPnl: 0,
      totalRepurchaseAmount: 0,
      totalRepurchaseCost: 0,
      totalHoursBalancing: 0,
      totalHoursPools: 0,
      totalPoolFeesUSDOrig: 0,
      currentPrice: 0,
      decimals: 2,
      weightedAvgInitialPrice: 0,
    };

    const totals = bots.reduce((acc, bot) => {
      const s = bot.stats;
      const isRskBot = s.botName.toLowerCase().startsWith("rsk");

      acc.totalCapital += s.totalCapitalUSD;
      acc.totalPnlUsd += s.pnlUSD;
      acc.poolFees += s.totalPoolFeesUSDOrig || 0;
      acc.lendingPnl += s.lendingPnl || 0;
      acc.botPremiumsUSD += s.botPremiumsUSD || 0;
      acc.manualPremiums += s.manualPremiums || 0;
      // Backend USD value of repurchased tokens (amount × token USD price), as in total capital.
      acc.buybacks += !isRskBot ? s.repurchaseCurValue || 0 : 0;
      // Capital the percentages are measured on: bots with a positive basis (see hasReturnBasis).
      if (hasReturnBasis(s)) acc.initialCapital += capitalBasis(s);
      acc.totalHoursBalancing += s.totalHoursBalancing || 0;
      acc.totalHoursPools += s.totalPoolHours || 0;
      acc.totalPoolFeesUSDOrig += s.totalPoolFeesUSDOrig || 0;
      acc.token = s.repurchaseToken;
      acc.decimals = bot.stats.decimals || 2;
      acc.currentPrice = s.currentPrice;
      if (s.repurchaseAmount && s.repurchaseAvgPrice) {
        if (!isRskBot) {
          acc.totalRepurchaseAmount += s.repurchaseAmount;
          acc.totalRepurchaseCost += s.repurchaseAmount * s.repurchaseAvgPrice;
        }
      }
      if (isInRange(s)) {
        acc.botsInRange += 1;
      }
      return acc;
    }, initial);

    // Same basis as every other return in the app (initial + deposits − withdrawals), over the bots
    // that have a positive one; the USD total keeps every bot.
    const overallPnlPercent = aggregateReturn(bots.map((b) => b.stats));

    // Initial price weighted by each bot's capital basis (deposits included).
    let totalInitialCapital = 0;
    let weightedInitialPriceSum = 0;
    bots.forEach((bot) => {
      const s = bot.stats;
      const w = Math.max(0, capitalBasis(s));
      totalInitialCapital += w;
      weightedInitialPriceSum += (s.initialPrice || 0) * w;
    });
    const weightedAvgInitialPrice =
      totalInitialCapital > 0
        ? weightedInitialPriceSum / totalInitialCapital
        : 0;

    const totalVolume = bots.reduce(
      (acc, bot) => acc + calculateTotalVolume(bot.positions),
      0
    );

    return {
      ...totals,
      overallPnlPercent,
      weightedAvgInitialPrice,
      totalVolume,
    };
  }, [bots]);

  const trends = React.useMemo(() => {
    const positions = bots.flatMap((b) => b.positions || []);
    return {
      volume: weeklyVolumeTrend(positions),
      fees: cumulativeFeesTrend(positions),
      // Treasury rule (no RSK buybacks) is applied inside; premiums count for every bot.
      buybacks: cumulativeBuybackTrend(bots),
    };
  }, [bots]);

  // vs HODL in percentage points = Σ hodlDiffUSD / Σ basis: exactly the cards' figure, and the
  // chart's last point by construction (its live point is pinned to the same sums).
  const vsHodl = vsHodlPp(bots);
  const lpReturn = aggregates.overallPnlPercent;
  const hodlReturn = lpReturn - vsHodl;

  const bonusYield = aggregates.buybacks + aggregates.manualPremiums;
  const shareOfCapital = (value: number) =>
    aggregates.initialCapital > 0 ? `${formatPercent(value / aggregates.initialCapital)} of capital` : undefined;
  const firstStats = bots[0]?.stats;
  const pairPrice = (v: number) =>
    formatPairPrice(v, firstStats?.token0Symbol, firstStats?.token1Symbol, aggregates.decimals);
  // Averages per calendar day since the first position (bot-hours would overstate a family's pace).
  const firstPositionAt = bots.reduce(
    (acc, b) => (b.positions || []).reduce((m, p) => (p.date > 0 && p.date < m ? p.date : m), acc),
    Infinity
  );
  const calendarDays = Number.isFinite(firstPositionAt) ? Math.max(1, (Date.now() - firstPositionAt) / 86_400_000) : 0;
  const perDay = (value: number) => (calendarDays > 0 ? formatUsd(value / calendarDays) : "—");
  const breakdown = pnlBreakdownDisplayed(aggregates.totalPnlUsd, aggregates.poolFees, bonusYield, aggregates.lendingPnl);
  const hodlDiffUsd = bots.reduce((acc, b) => acc + (b.stats.hodlDiffUSD || 0), 0);
  const benchmark = hodlBenchmarkLabel(
    bots.map((b) => b.stats),
    isHomeView
  );
  const holdsTokens = benchmark === "tokens deposited";

  const feesTooltip: TooltipSection[] | undefined = aggregates.poolFees > 0
    ? [
        {
          title: "Pool fees",
          items: [
            { label: "Total fees", value: formatUsd(aggregates.poolFees) },
            ...(Number.isFinite(firstPositionAt) ? [{ label: "Since", value: formatDate(firstPositionAt) }] : []),
            { label: "Average per day", value: perDay(aggregates.poolFees) },
          ],
        },
      ]
    : undefined;

  const bonusTooltip: TooltipSection[] | undefined = bonusYield > 0
    ? [
        {
          title: "Bonus yield",
          items: [
            { label: "Buybacks value", value: formatUsd(aggregates.buybacks) },
            { label: "Manual premiums", value: formatUsd(aggregates.manualPremiums) },
            { label: "Total", value: formatUsd(bonusYield) },
          ],
        },
        // Token count only within one family (the overview mixes tokens).
        ...(!isHomeView && aggregates.totalRepurchaseAmount > 0
          ? [
              {
                title: "Treasury",
                items: [
                  {
                    label: "Tokens bought",
                    value: `${formatQuantity(aggregates.totalRepurchaseAmount, aggregates.totalRepurchaseAmount >= 1000 ? 0 : 2)} ${aggregates.token}`,
                  },
                ],
              },
            ]
          : []),
      ]
    : undefined;

  // The backend's HODL benchmark is the pair price (base token priced in the quote token). Across
  // pairs those are different units, so the overview only states the summed USD difference.
  const hodlTooltip: TooltipSection[] = [
    {
      title: "LP vs HODL · since inception",
      items: isHomeView
        ? [
            { label: "Difference", value: formatSignedPp(vsHodl) },
            { label: "In USD", value: formatSignedUsd(hodlDiffUsd) },
            {
              label: "",
              value: holdsTokens
                ? "LP result minus simply holding the deposited tokens, summed in USD over the capital basis."
                : "Each bot's LP return minus its pair's price change, summed in USD over the capital basis.",
            },
          ]
        : [
            { label: "LP return", value: formatSignedPercent(lpReturn) },
            { label: `HODL (${benchmark})`, value: formatSignedPercent(hodlReturn) },
            { label: "Difference", value: `${formatSignedPp(vsHodl)} · ${formatSignedUsd(hodlDiffUsd)}` },
          ],
    },
    ...(!isHomeView
    ? [
        {
          title: "Initial price",
          items: [
            { label: "Weighted avg", value: pairPrice(aggregates.weightedAvgInitialPrice) },
            { label: "Current price", value: pairPrice(aggregates.currentPrice) },
          ],
        },
      ]
    : []),
  ];

  const capitalText = formatCurrency(aggregates.totalCapital, 0);
  const longFigure = capitalText.length > 11;
  const up = aggregates.totalPnlUsd >= 0;
  const states = countStates(bots);
  // What the strip shows, in words; phones keep only the most severe part so it never truncates.
  const rangeParts = [
    states["out-of-range"] > 0 && `${states["out-of-range"]} out of range`,
    states.idle > 0 && `${states.idle} no position`,
    states.balancing > 0 && `${states.balancing} balancing`,
  ].filter(Boolean) as string[];
  const rangeSub =
    aggregates.botsInRange === bots.length || rangeParts.length === 0 ? (
      "All positions in range"
    ) : (
      // Phones: one part per line (the tile has room above the strip); wider tiles: one line.
      <>
        <Box component="span" sx={{ display: { xs: "block", sm: "inline" } }}>
          {rangeParts[0]}
        </Box>
        {rangeParts.length > 1 && (
          <Box component="span" sx={{ display: { xs: "block", sm: "inline" } }}>
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              {" · "}
            </Box>
            {rangeParts[1]}
          </Box>
        )}
      </>
    );

  // Investors (non-admin) get a capital-at-work figure instead of operational bot states.
  // "Earning fees right now": only pools whose price is inside their range earn fees.
  const capitalInPools = bots
    .filter((b) => isInRange(b.stats))
    .reduce((acc, b) => acc + (b.stats.balanceSnapshotEnd?.poolValueUSD || 0), 0);
  const inPoolsPct = aggregates.totalCapital > 0 ? capitalInPools / aggregates.totalCapital : 0;
  const hasChart = performanceData.length > 1;

  return (
    <>
      <SurfaceCard
        strong
        radius="xl"
        tint={tokens.gold}
        sx={{ p: { xs: 2.5, sm: 3, md: 3.5, lg: 4.5 } }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: hasChart
              ? { xs: "minmax(0, 1fr)", md: "minmax(320px, 1fr) minmax(0, 1.4fr)" }
              : "minmax(0, 1fr)",
            gap: { xs: 3, md: 4, lg: 6 },
            alignItems: "start",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: "0.8125rem", md: "0.875rem" }, fontWeight: 500, color: "text.secondary" }}>
              Total capital
            </Typography>
            <Typography
              component="div"
              sx={{
                mt: { xs: 0.75, md: 1.25 },
                mb: { xs: 2, md: 2.5 },
                fontSize: longFigure
                  ? { xs: "2.25rem", sm: "2.75rem", md: "2.75rem", lg: "3.25rem", xl: "3.5rem" }
                  : { xs: "2.625rem", sm: "3.25rem", md: "3.125rem", lg: "3.75rem", xl: "4.125rem" },
                fontWeight: 650,
                lineHeight: 1.04,
                letterSpacing: "-0.045em",
                fontVariantNumeric: "lining-nums proportional-nums",
                whiteSpace: "nowrap",
                background: "linear-gradient(180deg, #FFFFFF 30%, #F1D3AF 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              {capitalText}
            </Typography>

            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75 }}>
              <CustomTooltip
                placement="bottom"
                sections={[
                  {
                    title: "Net change since inception",
                    items: [
                      { label: "Pool fees", value: formatSignedUsd(breakdown.fees) },
                      { label: "Bonus yield", value: formatSignedUsd(breakdown.bonus) },
                      ...(breakdown.lending !== 0 ? [{ label: "Lending", value: formatSignedUsd(breakdown.lending) }] : []),
                      { label: "Market & IL", value: formatSignedUsd(breakdown.market) },
                      { label: "Net", value: formatSignedUsd(aggregates.totalPnlUsd) },
                    ],
                  },
                  { title: "", items: [{ label: "", value: MARKET_RESIDUAL_HELP }] },
                ]}
              >
                <HeroChip
                  tone={aggregates.totalPnlUsd === 0 ? "neutral" : up ? "positive" : "negative"}
                  icon={
                    up ? (
                      <TrendingUpRoundedIcon sx={{ fontSize: 17 }} />
                    ) : (
                      <TrendingDownRoundedIcon sx={{ fontSize: 17 }} />
                    )
                  }
                >
                  {formatSignedPercent(aggregates.overallPnlPercent)} · {formatSignedCurrency(aggregates.totalPnlUsd, 0)}
                </HeroChip>
              </CustomTooltip>
              {isAdmin && (
                <CustomTooltip placement="bottom" sections={hodlTooltip}>
                  <HeroChip tone={Math.abs(vsHodl) < 0.00005 ? "neutral" : vsHodl > 0 ? "positive" : "negative"}>
                    {formatSignedPp(vsHodl)} vs HODL
                  </HeroChip>
                </CustomTooltip>
              )}
              {aggregates.lendingPnl > 0 && (
                <HeroChip tone="neutral">Lending {formatSignedCurrency(aggregates.lendingPnl, 0)}</HeroChip>
              )}
            </Stack>

            <Typography sx={{ mt: { xs: 2, md: 2.5 }, fontSize: "0.8125rem", color: "text.secondary" }}>
              Since inception · {formatCompactCurrency(aggregates.initialCapital)} net deposited
            </Typography>

            {attention.length > 0 && <AttentionRow items={attention} />}
          </Box>

          {hasChart && (
            <PerformanceChart
              variant="hero"
              data={performanceData}
              hodlLabel={`HODL · ${benchmark}`}
              pairTokens={isHomeView ? undefined : [firstStats?.token0Symbol, firstStats?.token1Symbol]}
              decimals={aggregates.decimals}
              height={{ xs: 170, sm: 200, md: 220, lg: 240 }}
            />
          )}
        </Box>
      </SurfaceCard>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
          gap: { xs: 1.5, md: 2 },
          mt: { xs: 1.5, md: 2 },
        }}
      >
        <StatTile
          icon={BarChartRoundedIcon}
          label="Volume provided"
          value={formatCompactCurrency(aggregates.totalVolume)}
          sub="Since inception"
          trend={trends.volume}
        />
        <StatTile
          icon={WaterDropRoundedIcon}
          label="Pool fees"
          value={aggregates.poolFees ? formatCompactCurrency(aggregates.poolFees) : "—"}
          sub={aggregates.poolFees ? shareOfCapital(aggregates.poolFees) : undefined}
          trend={trends.fees}
          tooltipSections={feesTooltip}
        />
        <StatTile
          icon={AutoAwesomeRoundedIcon}
          label="Bonus yield"
          value={bonusYield ? formatCompactCurrency(bonusYield) : "—"}
          sub={bonusYield ? shareOfCapital(bonusYield) : "No buybacks or premiums"}
          trend={trends.buybacks}
          tooltipSections={bonusTooltip}
        />
        {isAdmin ? (
          <StatTile
            icon={TrackChangesRoundedIcon}
            label="Bots in range"
            value={`${aggregates.botsInRange}/${bots.length}`}
            sub={rangeSub}
            footer={<BotStateStrip bots={bots} order={isHomeView ? "severity" : "bot"} />}
          />
        ) : (
          <StatTile
            icon={TrackChangesRoundedIcon}
            label="Capital in pools"
            value={`${(inPoolsPct * 100).toFixed(1)}%`}
            sub="Earning fees right now"
            footer={
              <Box sx={{ height: 8, borderRadius: 999, bgcolor: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                <Box sx={{ width: `${Math.min(100, inPoolsPct * 100)}%`, height: "100%", borderRadius: 999, background: tokens.goldGradient }} />
              </Box>
            }
          />
        )}
      </Box>
    </>
  );
};
